import React, { useEffect, useRef, useCallback } from 'react';
import './StepPagination.css';

/**
 * Props for StepPagination component
 * 
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5
 */
export interface StepPaginationProps {
  /** Current page index (0-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of steps per page (typically 16) */
  stepsPerPage: number;
  /** Total number of steps in the composition */
  totalSteps: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
}

/**
 * StepPagination Component
 * 
 * Provides navigation controls for moving between step pages when the composition
 * has more than 16 steps. Displays page indicator showing current step range
 * (e.g., "Steps 1-16", "Steps 17-32") and provides previous/next arrow buttons.
 * Supports swipe left/right gestures for page navigation.
 * 
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5
 */
export const StepPagination: React.FC<StepPaginationProps> = ({
  currentPage,
  totalPages,
  stepsPerPage,
  totalSteps,
  onPageChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  /**
   * Calculate start and end step numbers for current page
   * Requirement 10.5: Display page indicator showing current range
   */
  const startStep = currentPage * stepsPerPage + 1;
  const endStep = Math.min((currentPage + 1) * stepsPerPage, totalSteps);
  const pageLabel = `Steps ${startStep}-${endStep}`;

  /**
   * Handle previous page navigation
   * Requirement 10.4: Provide previous arrow button for page navigation
   */
  const isNavigatingRef = useRef(false);
  
  const handlePreviousPage = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent rapid-fire navigation
    if (isNavigatingRef.current) {
      console.log('[StepPagination] handlePreviousPage BLOCKED - already navigating');
      return;
    }
    
    console.log('[StepPagination] handlePreviousPage called:', { currentPage, canGoPrevious: currentPage > 0 });
    if (currentPage > 0) {
      isNavigatingRef.current = true;
      const newPage = currentPage - 1;
      console.log('[StepPagination] MANUAL NAVIGATION: Going to page', newPage);
      onPageChange(newPage);
      
      // Reset navigation lock after a short delay
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    } else {
      console.log('[StepPagination] Cannot go to previous page - already at page 0');
    }
  }, [currentPage, onPageChange]);

  /**
   * Handle next page navigation
   * Requirement 10.4: Provide next arrow button for page navigation
   */
  const handleNextPage = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent rapid-fire navigation
    if (isNavigatingRef.current) {
      console.log('[StepPagination] handleNextPage BLOCKED - already navigating');
      return;
    }
    
    console.log('[StepPagination] handleNextPage called:', { currentPage, totalPages, canGoNext: currentPage < totalPages - 1 });
    if (currentPage < totalPages - 1) {
      isNavigatingRef.current = true;
      const newPage = currentPage + 1;
      console.log('[StepPagination] MANUAL NAVIGATION: Going to page', newPage);
      onPageChange(newPage);
      
      // Reset navigation lock after a short delay
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    } else {
      console.log('[StepPagination] Cannot go to next page - already at last page');
    }
  }, [currentPage, totalPages, onPageChange]);

  /**
   * Handle touch start event for swipe gesture
   * Requirement 10.3: Support swipe left/right gestures for page navigation
   */
  const handleTouchStart = (e: TouchEvent) => {
    // Ignore touches on buttons to prevent interference with button clicks
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      console.log('[StepPagination] Touch start on button - ignoring for swipe');
      touchStartX.current = 0;
      touchEndX.current = 0;
      return;
    }
    
    if (e.touches[0]) {
      touchStartX.current = e.touches[0].clientX;
      console.log('[StepPagination] Touch start:', touchStartX.current);
    }
  };

  /**
   * Handle touch move event for swipe gesture
   * Requirement 10.3: Support swipe left/right gestures for page navigation
   */
  const handleTouchMove = (e: TouchEvent) => {
    // Only track if we have a valid touch start (not on a button)
    if (touchStartX.current === 0) {
      return;
    }
    
    if (e.touches[0]) {
      touchEndX.current = e.touches[0].clientX;
    }
  };

  /**
   * Handle touch end event for swipe gesture
   * Requirement 10.3: Support swipe left/right gestures for page navigation
   * 
   * Swipe left (touchStartX > touchEndX + threshold) = Next page
   * Swipe right (touchStartX < touchEndX - threshold) = Previous page
   */
  const handleTouchEnd = useCallback(() => {
    const swipeThreshold = 50; // Minimum distance for swipe detection
    const deltaX = touchStartX.current - touchEndX.current;

    console.log('[StepPagination] handleTouchEnd:', { 
      touchStartX: touchStartX.current, 
      touchEndX: touchEndX.current, 
      deltaX, 
      threshold: swipeThreshold,
      willTrigger: Math.abs(deltaX) > swipeThreshold
    });

    if (Math.abs(deltaX) > swipeThreshold) {
      if (deltaX > 0) {
        // Swipe left - next page
        console.log('[StepPagination] SWIPE DETECTED: Next page');
        handleNextPage();
      } else {
        // Swipe right - previous page
        console.log('[StepPagination] SWIPE DETECTED: Previous page');
        handlePreviousPage();
      }
    } else {
      console.log('[StepPagination] Touch end - no swipe detected (below threshold)');
    }

    // Reset touch positions
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [handleNextPage, handlePreviousPage]);

  /**
   * Set up touch event listeners for swipe gestures
   * Requirement 10.3: Support swipe left/right gestures for page navigation
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchEnd]); // Only re-attach when handleTouchEnd changes

  /**
   * Handle keyboard navigation
   * Arrow Left = Previous page
   * Arrow Right = Next page
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePreviousPage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNextPage();
    }
  };

  return (
    <div
      ref={containerRef}
      className="step-pagination"
      role="navigation"
      aria-label="Step page navigation"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Previous Page Button - Requirement 10.4 */}
      <button
        className="pagination-btn pagination-btn-prev"
        onTouchEnd={(e) => {
          e.preventDefault();
          handlePreviousPage(e);
        }}
        onClick={(e) => {
          // Only handle click if not a touch device (prevents double-firing)
          if (!('ontouchstart' in window)) {
            handlePreviousPage(e);
          }
        }}
        disabled={currentPage === 0}
        aria-label="Previous page"
        title="Previous page"
      >
        <i className="fa-solid fa-chevron-left"></i>
      </button>

      {/* Page Indicator - Requirement 10.5 */}
      <div className="pagination-indicator" aria-live="polite" aria-atomic="true">
        <span className="pagination-label">{pageLabel}</span>
        <span className="pagination-page-info">
          Page {currentPage + 1} of {totalPages}
        </span>
      </div>

      {/* Next Page Button - Requirement 10.4 */}
      <button
        className="pagination-btn pagination-btn-next"
        onTouchEnd={(e) => {
          e.preventDefault();
          handleNextPage(e);
        }}
        onClick={(e) => {
          // Only handle click if not a touch device (prevents double-firing)
          if (!('ontouchstart' in window)) {
            handleNextPage(e);
          }
        }}
        disabled={currentPage === totalPages - 1}
        aria-label="Next page"
        title="Next page"
      >
        <i className="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  );
};
